import _jsx from "@babel/runtime/helpers/jsx";
import PropTypes from 'prop-types';
import React, { lazy, Suspense, useEffect, memo, useCallback } from 'react';
import { connect, useSelector, useDispatch } from 'react-redux';
import { mapDispatchToActionProp } from '@atc/modular-redux';
import { authUtilityLoader } from '@consumer/auth-utility-loader';
import _get from 'lodash/get';
import _isEqual from 'lodash/isEqual';
import { HeaderBadge } from 'reaxl-header-badge';
import { GlobalAlert } from 'reaxl';
import { QuickSaveNotification, UpdateSavedSearch } from 'reaxl-molecules';
import { sendClick } from 'reaxl-analytics';
import { messagesHeaderLinkClick } from 'reaxl-analytics-handlers';
import { useFeatures } from 'reaxl-features';
import { useDevice } from '@atc/react-device';
import EmailSubscribe from '../EmailSubscribe';
const UpdateSavedInventory = /*#__PURE__*/lazy(() => import(/* webpackChunkName: "authenticationHeaderUpdateSavedInventory" */
'./AuthenticationHeaderUpdateSavedInventory'));
const QuickSaveList = /*#__PURE__*/lazy(() => import(/* webpackChunkName: "authenticationHeaderQuickSaveList" */
'./QuickSaveList'));
const authEventsOn = async (eventName, handler) => {
  const {
    on
  } = await authUtilityLoader();
  on(eventName, handler);
};
const authEventsOff = async (eventName, handler) => {
  const {
    off
  } = await authUtilityLoader();
  off(eventName, handler);
};
/**
 * AuthenticationHeader
 * Enables QuickSave and MyATC (Save listing/search) functionality
 */
function AuthenticationHeader(_ref) {
  var _Suspense, _QuickSaveNotificatio, _EmailSubscribe;
  let {
    actions = {},
    displayWalletModal,
    enableMyWallet = false,
    navItems,
    onSignUpSave,
    onSignInSave,
    showEmailSubscribe,
    showUserState,
    userDuck,
    savedInventoryDuck,
    savedInventory,
    savedInventoryAlert,
    onSignInClick = () => {},
    onSignOutClick = () => {},
    onQuickSaveItemClick = () => {},
    onQuickSaveItemToggle = () => {},
    onShowQuickSaveList = () => {},
    onLoggedInStateReady = () => {},
    onSaveSearchUpdate = () => {},
    onSaveInventoryUpdate = () => {},
    onShareListingsClick,
    onEmailSubscribe = () => {},
    onEmailAlertToggle = () => {},
    onTextAlertToggle = () => {},
    paymentsDuck,
    dealsDuck,
    isLoggedIn = false,
    savedInventoryCount = 0,
    isQuickSaveListVisible = false,
    showQuickSaveNotification = false,
    updateSavedInventoryState = {},
    recentSearchCount = true,
    profile = {},
    onClick = () => {},
    hasNewTradeIn = false,
    valuationHistory,
    numCreditAppDeals = 0,
    dealNotifications = {},
    isTredEnable,
    animatedButtonState,
    dealDetails,
    pageRenderId = '',
    suppressGoogleOneTap = false,
    showPsxNotificationsCount = false,
    showMyCars = true,
    useAuthRedirect = false,
    clearDataOnSignOut = true
  } = _ref;
  const {
    hidePersonalizationAlert,
    hideSavedInventoryAlert,
    hideQuickSaveList,
    hideQuickSaveNotification,
    hideUpdateSavedInventory,
    hideUpdateSavedSearch,
    hydrateUserInventory,
    hydrateRecentSearches,
    saveInventoryItem,
    updateMyWallet,
    unsaveInventoryItem,
    clearSavedInventory,
    clearProfile,
    updateSavedInventoryItem,
    updateSavedSearchItem,
    setMaxQuickSavesLimit,
    hydratePsxNotifications,
    hydrateMyCars
  } = actions;
  const dispatch = useDispatch();
  const updateSavedSearchState = useSelector(userDuck.selectors.getUpdateSavedSearchState);
  const showUpdateSavedSearch = useSelector(userDuck.selectors.getShowUpdateSavedSearch);
  const userPhone = useSelector(userDuck.selectors.getUserPhone);
  const listings = useSelector(savedInventoryDuck?.selectors.getSavedInventory);
  const personalizationAlert = useSelector(userDuck.selectors.getPersonalizationAlert);
  const savedSearchCount = useSelector(userDuck.selectors.getSavedSearchCount);
  const psxNotificationsCount = useSelector(userDuck.selectors.getPsxNotificationsCount);
  const myCarsCount = useSelector(userDuck.selectors.getMyCarsCount);
  const {
    active_deals_my_wallet: [enableActiveDeals],
    my_wallet: [, {
      enable_deals: enableDeals
    }],
    show_wallet_alert_notification: [showWalletAlertNotification],
    standalone_credit_app: [enableStandaloneCreditApp]
  } = useFeatures(['active_deals_my_wallet', 'my_wallet', 'show_wallet_alert_notification', 'standalone_credit_app']);
  const maxQuickSavesReached = !isLoggedIn && savedInventoryDuck.selectors.hasMaxQuickSaveItems({
    savedInventory
  });
  const maxQuickSavesLimit = 0;
  const device = useDevice();
  const isXs = _get(device, 'is.xs', true);
  const showWalletAlertFlag = showWalletAlertNotification && !isXs;
  const showMyCarsTabOnDropdown = showMyCars;
  useEffect(() => {
    setMaxQuickSavesLimit(maxQuickSavesLimit);
    hydrateRecentSearches({
      withCounts: recentSearchCount
    });
    if (!suppressGoogleOneTap) {
      (async () => {
        const {
          loadGoogleOneTap
        } = await authUtilityLoader();
        loadGoogleOneTap();
      })();
    }

    // These functions dispatch redux actions that will be resolved by the authDuck if it is present (common) and ignored if it is absent (uncommon).
    const setLoggedIn = async () => {
      dispatch({
        type: 'auth/SET_LOGGED_IN'
      });
    };
    const setLoggedOut = () => {
      dispatch({
        type: 'auth/SET_LOGGED_OUT'
      });
      if (clearDataOnSignOut) {
        clearSavedInventory();
        clearProfile();
      }
    };
    authEventsOn('auth.signin', setLoggedIn);
    authEventsOn('auth.signout', setLoggedOut);
    return () => {
      authEventsOff('auth.signin', setLoggedIn);
      authEventsOff('auth.signout', setLoggedOut);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageRenderId]);
  useEffect(() => {
    hydrateUserInventory({
      isLoggedIn,
      onLoggedInStateReady
    });
    if (isLoggedIn) {
      if (showPsxNotificationsCount) {
        hydratePsxNotifications();
      }
      if (showMyCarsTabOnDropdown) {
        hydrateMyCars();
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);
  const handleShowQuickSaveList = useCallback((e, data) => {
    onClick(e, {
      ...data,
      linkContext: 'myAtc'
    });
    onShowQuickSaveList();
  }, [onClick, onShowQuickSaveList]);
  const handleQuickSaveItemClick = useCallback((event, listing) => {
    onQuickSaveItemClick(event, listing);
  }, [onQuickSaveItemClick]);
  const handleQuickSaveToggle = useCallback((event, listing, wasSaved) => {
    if (wasSaved) {
      unsaveInventoryItem(listing);
    } else {
      saveInventoryItem(listing);
    }
    onQuickSaveItemToggle(event, listing, wasSaved);
  }, [onQuickSaveItemToggle, saveInventoryItem, unsaveInventoryItem]);
  const handleSaveSearchUpdate = () => {
    let disable = false;
    return async data => {
      if (!disable) {
        disable = true;
        await updateSavedSearchItem(data);
        onSaveSearchUpdate(data);
        disable = false;
      }
    };
  };
  const handleSaveInventoryUpdate = () => {
    let disable = false;
    return async data => {
      if (!disable) {
        disable = true;
        await updateSavedInventoryItem(data);
        onSaveInventoryUpdate(data);
        disable = false;
      }
    };
  };
  const handleWalletTrigger = useCallback(event => {
    onClick(event, {
      linkContext: 'myWallet'
    });
    updateMyWallet({
      displayModal: !displayWalletModal
    });
  }, [onClick, updateMyWallet, displayWalletModal]);
  const handleMessagesClick = useCallback((event, data) => {
    sendClick(messagesHeaderLinkClick, event, {});
    onClick(event, {
      ...data,
      linkContext: 'myAtc'
    });
  }, [onClick]);
  const handleRedirect = _ref2 => {
    let {
      path
    } = _ref2;
    const search = new URLSearchParams(window.location.search);
    const redirectUrl = search ? `${path}?${search.toString()}` : path;
    window.location.assign(redirectUrl);
  };
  const handleLogIn = useCallback(async (e, data) => {
    if (useAuthRedirect) {
      return handleRedirect({
        path: '/account/sign-in'
      });
    }
    const {
      showAuthModal
    } = await authUtilityLoader();
    onClick(e, {
      ...data,
      linkContext: 'signin'
    });
    return showAuthModal({
      screen: 'signin',
      signUpCustomBodyText: 'The only place to find great car values based on Kelley Blue Book® Fair Market Ranges.'
    });
  }, [onClick, useAuthRedirect]);
  const handleSignUp = useCallback(async (event, data) => {
    if (useAuthRedirect) {
      return handleRedirect({
        path: '/account/sign-up'
      });
    }
    onClick(event, {
      ...data,
      linkContext: 'signup'
    });
    const {
      showAuthModal
    } = await authUtilityLoader();
    return showAuthModal({
      screen: 'signup'
    });
  }, [onClick, useAuthRedirect]);
  const handleLogOut = useCallback(async (e, data) => {
    const {
      signOut
    } = await authUtilityLoader();
    const action = 'signout';
    onClick(e, {
      ...data,
      label: 'Sign Out',
      linkContext: action
    });
    await signOut();
    onSignOutClick();
  }, [onClick, onSignOutClick]);
  const handleOnToggle = useCallback((e, data) => {
    onClick(e, {
      ...data,
      linkContext: 'myAtcMenuToggle'
    });
  }, [onClick]);
  const handleSavedSearchesClick = useCallback((e, data) => {
    onClick(e, {
      ...data,
      linkContext: 'myAtc'
    });
  }, [onClick]);
  const handleMyCarsClick = useCallback((e, data) => {
    onClick(e, {
      ...data,
      linkContext: 'myAtc'
    });
  }, [onClick]);
  const handleManageMyAds = useCallback((e, data) => {
    onClick(e, {
      ...data,
      linkContext: 'myAtc'
    });
  }, [onClick]);
  const handleProfile = useCallback((e, data) => {
    onClick(e, {
      ...data,
      linkContext: 'myAtc'
    });
  }, [onClick]);
  const handleSignIn = useCallback(e => {
    onClick(e, {
      linkContext: 'myAtcSignIn'
    });
  }, [onClick]);
  const handleHeaderWalletPopoverClick = useCallback(e => {
    updateMyWallet({
      displayModal: true,
      selectedPanelName: e.target.dataset.panel
    });
  }, [updateMyWallet]);
  const renderQuickSaveList = useCallback(() => _Suspense || (_Suspense = /*#__PURE__*/_jsx(Suspense, {
    fallback: null
  }, void 0, /*#__PURE__*/_jsx(QuickSaveList, {
    onSignInClick: handleLogIn,
    listings: listings,
    show: isQuickSaveListVisible,
    onHide: hideQuickSaveList,
    onQuickSaveToggle: handleQuickSaveToggle,
    onQuickSaveItemClick: handleQuickSaveItemClick,
    onShareListingsClick: onShareListingsClick
  }, "quickSaveList"))), [handleLogIn, listings, isQuickSaveListVisible, hideQuickSaveList, handleQuickSaveToggle, handleQuickSaveItemClick, onShareListingsClick]);
  const renderQuickSaveNotification = useCallback(() => _QuickSaveNotificatio || (_QuickSaveNotificatio = /*#__PURE__*/_jsx(QuickSaveNotification, {
    savedInventoryCount: savedInventoryCount,
    show: showQuickSaveNotification,
    isMaxSaved: maxQuickSavesReached,
    onHide: hideQuickSaveNotification,
    onSignUpClick: handleSignUp
  }, "quickSaveNotification")), [showQuickSaveNotification, savedInventoryCount, maxQuickSavesReached, hideQuickSaveNotification, handleSignUp]);
  const renderUpdateSavedInventory = () => /*#__PURE__*/_jsx(Suspense, {
    fallback: null
  }, void 0, /*#__PURE__*/React.createElement(UpdateSavedInventory, Object.assign({
    key: "updateSavedInventory"
  }, updateSavedInventoryState, {
    btnContext: updateSavedInventoryState.animatedButtonState,
    onHide: hideUpdateSavedInventory,
    onSubmit: handleSaveInventoryUpdate(),
    onEmailAlertToggle: onEmailAlertToggle,
    onTextAlertToggle: onTextAlertToggle
  })));
  const renderUpdateSavedSearch = () => /*#__PURE__*/React.createElement(UpdateSavedSearch, Object.assign({
    key: "updateSavedSearch"
  }, updateSavedSearchState, {
    show: showUpdateSavedSearch,
    phone: userPhone,
    onHide: hideUpdateSavedSearch,
    onSubmit: handleSaveSearchUpdate(),
    animatedButtonState: animatedButtonState
  }));
  const showMyWalletIcon = !!updateMyWallet && enableMyWallet;
  const renderHeaderBadge = useCallback(() => showUserState && /*#__PURE__*/_jsx(HeaderBadge, {
    navItems: navItems,
    isLoggedIn: isLoggedIn,
    showMessages: true,
    showMyCars: showMyCarsTabOnDropdown,
    myCarsCount: myCarsCount,
    onMessagesClick: handleMessagesClick,
    showWallet: showMyWalletIcon,
    onWalletTrigger: handleWalletTrigger,
    savedInventoryCount: savedInventoryCount,
    savedSearchCount: savedSearchCount,
    profile: {
      name: profile.firstName,
      thumbnailUrl: profile.thumbnailUrl,
      firstName: profile.firstName,
      lastName: profile.lastName
    },
    onLogIn: handleLogIn,
    onLogOut: handleLogOut,
    onSignUp: handleSignUp,
    onQuickSaveCountClick: handleShowQuickSaveList,
    onSavedSearchesClick: handleSavedSearchesClick,
    onMyCarsClick: handleMyCarsClick,
    onManageMyAdClick: handleManageMyAds,
    onProfileClick: handleProfile,
    onSignIn: handleSignIn,
    onHeaderWalletPopoverClick: handleHeaderWalletPopoverClick,
    paymentsDuck: paymentsDuck,
    dealsDuck: dealsDuck,
    hasNewTradeIn: hasNewTradeIn,
    valuationHistory: valuationHistory,
    numCreditAppDeals: enableStandaloneCreditApp && numCreditAppDeals,
    dealNotifications: dealNotifications,
    enableActiveDeals: enableActiveDeals,
    isTredEnable: isTredEnable,
    dealDetails: enableDeals ? dealDetails : undefined,
    showWalletAlertFlag: showWalletAlertFlag,
    psxNotificationsCount: psxNotificationsCount,
    onHeaderToggle: handleOnToggle
  }, "headerBadge"), [showUserState, navItems, isLoggedIn, showMyCarsTabOnDropdown, myCarsCount, handleMessagesClick, showMyWalletIcon, handleWalletTrigger, savedInventoryCount, savedSearchCount, profile.firstName, profile.thumbnailUrl, profile.lastName, handleLogIn, handleLogOut, handleSignUp, handleShowQuickSaveList, handleSavedSearchesClick, handleMyCarsClick, handleManageMyAds, handleProfile, handleSignIn, handleHeaderWalletPopoverClick, paymentsDuck, dealsDuck, hasNewTradeIn, valuationHistory, enableStandaloneCreditApp, numCreditAppDeals, dealNotifications, enableActiveDeals, isTredEnable, enableDeals, dealDetails, showWalletAlertFlag, psxNotificationsCount, handleOnToggle]);
  const renderPersonalizationAlert = useCallback(() => {
    const {
      bsStyle,
      message
    } = personalizationAlert;
    return /*#__PURE__*/_jsx(GlobalAlert, {
      show: !!message,
      bsStyle: bsStyle,
      onHide: hidePersonalizationAlert
    }, void 0, message);
  }, [personalizationAlert, hidePersonalizationAlert]);
  const renderSavedInventoryAlert = useCallback(() => {
    const {
      bsStyle,
      message
    } = savedInventoryAlert;
    return /*#__PURE__*/_jsx(GlobalAlert, {
      show: !!message,
      bsStyle: bsStyle,
      onHide: hideSavedInventoryAlert
    }, void 0, message);
  }, [savedInventoryAlert, hideSavedInventoryAlert]);
  const renderEmailSubscribe = useCallback(() => _EmailSubscribe || (_EmailSubscribe = /*#__PURE__*/_jsx(EmailSubscribe, {
    onSubmit: onEmailSubscribe,
    onSignInSave: onSignInSave,
    onSignUpSave: onSignUpSave,
    userDuck: userDuck
  }, "emailSubscribe")), [onSignUpSave, onSignInSave, onEmailSubscribe, userDuck]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, renderHeaderBadge(), !isLoggedIn && isQuickSaveListVisible && renderQuickSaveList(), !isLoggedIn && renderQuickSaveNotification(), updateSavedInventoryState.show && renderUpdateSavedInventory(), renderUpdateSavedSearch(), personalizationAlert && renderPersonalizationAlert(), savedInventoryAlert && renderSavedInventoryAlert(), showEmailSubscribe && renderEmailSubscribe());
}
function mapStateToProps(state, _ref3) {
  let {
    userDuck,
    dealsDuck,
    paymentsDuck,
    savedInventoryDuck
  } = _ref3;
  const {
    user: {
      showQuickSaveList,
      showEmailSubscribeModal,
      showEmailSubscribeSuccess,
      lightRegistrationError,
      profile,
      showUserState
    },
    savedInventory,
    savedInventory: {
      showQuickSaveNotification,
      animatedButtonState
    },
    payments: {
      tradeIn: {
        hasNewTradeIn,
        valuationHistory
      },
      creditAppDeals
    }
  } = state;
  const userSavedListings = _get(savedInventory, 'inventory', []);
  const savedListingId = userSavedListings.length ? userSavedListings.slice(-1).pop() : null;
  const {
    displayModal
  } = paymentsDuck?.creators?.getMyWallet?.(state) || {};
  const dealNotifications = dealsDuck?.selectors?.getDealNotifications?.(state) || {};
  const dealDetails = dealsDuck?.selectors?.getTransformedDealDetails?.(state) || {};
  return {
    displayWalletModal: displayModal || false,
    isQuickSaveListVisible: showQuickSaveList,
    lightRegistrationError,
    profile,
    savedInventory,
    updateSavedInventoryState: savedInventoryDuck?.selectors.getUpdateSavedInventoryState({
      savedInventory
    }),
    savedInventoryCount: savedInventoryDuck?.selectors.getSavedInventoryCount({
      savedInventory
    }),
    savedInventoryAlert: savedInventoryDuck?.selectors?.getSavedInventoryAlert(state) || {},
    dealDetails,
    savedListingId,
    showEmailSubscribeModal,
    showEmailSubscribeSuccess,
    showQuickSaveNotification,
    showUserState,
    hasNewTradeIn,
    valuationHistory,
    numCreditAppDeals: creditAppDeals?.length,
    animatedButtonState,
    dealNotifications
  };
}
const mapDispatchToProps = (dispatch, _ref4) => {
  let {
    userDuck,
    paymentsDuck,
    recentSearchDuck,
    savedInventoryDuck
  } = _ref4;
  return mapDispatchToActionProp({
    saveInventoryItem: savedInventoryDuck?.creators?.saveInventoryItem,
    unsaveInventoryItem: savedInventoryDuck?.creators?.unsaveInventoryItem,
    clearSavedInventory: savedInventoryDuck?.creators?.clearSavedInventory,
    showQuickSaveList: userDuck.creators.showQuickSaveList,
    hideQuickSaveList: userDuck.creators.hideQuickSaveList,
    hideQuickSaveNotification: savedInventoryDuck?.creators?.hideQuickSaveNotification,
    hideUpdateSavedInventory: savedInventoryDuck?.creators?.hideUpdateSavedInventory,
    hideUpdateSavedSearch: userDuck.creators.hideUpdateSavedSearch,
    updateMyWallet: paymentsDuck?.creators?.updateMyWallet,
    updateSavedInventoryItem: savedInventoryDuck?.creators?.updateSavedInventoryItem,
    updateSavedSearchItem: userDuck.creators.updateSavedSearchItem,
    hydratePsxNotifications: userDuck.creators.hydratePsxNotifications,
    hydrateMyCars: userDuck.creators.hydrateMyCars,
    hydrateRecentSearches: recentSearchDuck?.creators.hydrateRecentSearches,
    hydrateUserInventory: userDuck.creators.hydrateUserInventory,
    hidePersonalizationAlert: userDuck.creators.clearGlobalAlert,
    hideSavedInventoryAlert: savedInventoryDuck?.creators?.clearGlobalAlert,
    hideEmailSubscribeModal: userDuck.creators.hideEmailSubscribeModal,
    setMaxQuickSavesLimit: savedInventoryDuck?.creators?.setMaxQuickSavesLimit,
    clearProfile: userDuck.creators.clearProfile
  });
};
export default connect(mapStateToProps, mapDispatchToProps)(/*#__PURE__*/memo(AuthenticationHeader, _isEqual));